<script>
	// gen2 음원 이미지 피드백 스레드.
	// 음원 단위로 쌓이며 답글은 1단계만, 최상위는 해결 표시를 가진다.
	// 이 프로젝트는 컴포넌트 <style> 이 번들에 로드되지 않으므로 인라인 스타일 + Bootstrap 만 쓴다.
	import { createEventDispatcher } from "svelte";
	import { fade } from "svelte/transition";
	import { ErrorCodes } from "../define/define";
	import {
		getApi,
		getSoundImageFeedbacksGen2,
		createSoundImageFeedbackGen2,
		updateSoundImageFeedbackGen2,
		deleteSoundImageFeedbackGen2,
		resolveSoundImageFeedbackGen2,
	} from "../service/api";

	export let idSound = 0;

	// 백엔드 검증과 동일하게 유지
	const MAX_CONTENT_LENGTH = 2000;

	const dispatch = createEventDispatcher();

	let items = [];
	let total = 0;
	let unresolvedCount = 0;
	let isLoading = false;
	let isSaving = false;

	// 새 피드백 입력
	let newContent = "";

	// 답글 입력 — 열려 있는 최상위 피드백 id
	let replyingToId = null;
	let replyContent = "";

	// 인라인 수정 — 편집 중인 피드백 id
	let editingId = null;
	let editContent = "";

	// idSound 가 바뀌면 다시 불러온다
	$: if (idSound) {
		loadFeedbacks(idSound);
	}

	// 목록 조회
	const loadFeedbacks = async (targetId) => {
		isLoading = true;
		resetInputs();
		try {
			const response = await getApi(getSoundImageFeedbacksGen2(targetId));
			if (response.resultCode !== ErrorCodes.OK) {
				items = [];
				total = 0;
				unresolvedCount = 0;
				return;
			}
			const data = response.data || {};
			items = data.list || [];
			total = data.total || 0;
			unresolvedCount = data.unresolvedCount || 0;
			dispatch("countChange", { total, unresolvedCount });
		} finally {
			isLoading = false;
		}
	};

	// 입력 상태 초기화
	const resetInputs = () => {
		newContent = "";
		replyingToId = null;
		replyContent = "";
		editingId = null;
		editContent = "";
	};

	// 서버 재조회 없이 집계를 다시 계산한다
	const recountFromItems = () => {
		total = items.reduce((sum, top) => sum + (top.isDeleted ? 0 : 1) + (top.replies?.length || 0), 0);
		unresolvedCount = items.filter((top) => !top.isDeleted && !top.isResolved).length;
		dispatch("countChange", { total, unresolvedCount });
	};

	// 본문 검증
	const validateContent = (text) => {
		const trimmed = (text || "").trim();
		if (!trimmed) {
			alert("피드백 내용을 입력하세요.");
			return null;
		}
		if (trimmed.length > MAX_CONTENT_LENGTH) {
			alert(`피드백은 ${MAX_CONTENT_LENGTH}자까지 입력할 수 있습니다.`);
			return null;
		}
		return trimmed;
	};

	// 최상위 피드백 작성
	const createFeedback = async () => {
		const content = validateContent(newContent);
		if (!content) return;

		isSaving = true;
		try {
			const result = await createSoundImageFeedbackGen2({ idSound, content, idParent: null });
			if (result.resultCode !== ErrorCodes.OK) {
				alert(result.resultDescKo || "피드백 등록에 실패했습니다.");
				return;
			}
			items = [...items, { ...result.data, replies: result.data.replies || [] }];
			newContent = "";
			recountFromItems();
		} catch (error) {
			alert("피드백 등록 중 오류가 발생했습니다.");
		} finally {
			isSaving = false;
		}
	};

	// 답글 작성
	const createReply = async (parent) => {
		const content = validateContent(replyContent);
		if (!content) return;

		isSaving = true;
		try {
			const result = await createSoundImageFeedbackGen2({ idSound, content, idParent: parent.id });
			if (result.resultCode !== ErrorCodes.OK) {
				alert(result.resultDescKo || "답글 등록에 실패했습니다.");
				return;
			}
			items = items.map((top) =>
				top.id === parent.id ? { ...top, replies: [...(top.replies || []), result.data] } : top
			);
			replyingToId = null;
			replyContent = "";
			recountFromItems();
		} catch (error) {
			alert("답글 등록 중 오류가 발생했습니다.");
		} finally {
			isSaving = false;
		}
	};

	// 인라인 수정 시작
	const startEdit = (feedback) => {
		editingId = feedback.id;
		editContent = feedback.content;
	};

	// 인라인 수정 취소
	const cancelEdit = () => {
		editingId = null;
		editContent = "";
	};

	// 수정 저장
	const saveEdit = async (feedback) => {
		const content = validateContent(editContent);
		if (!content) return;

		isSaving = true;
		try {
			const result = await updateSoundImageFeedbackGen2({ id: feedback.id, content });
			if (result.resultCode !== ErrorCodes.OK) {
				alert(result.resultDescKo || "피드백 수정에 실패했습니다.");
				return;
			}
			items = items.map((top) => {
				if (top.id === feedback.id) {
					return { ...top, ...result.data, replies: top.replies || [] };
				}
				if (top.replies?.some((reply) => reply.id === feedback.id)) {
					return {
						...top,
						replies: top.replies.map((reply) => (reply.id === feedback.id ? { ...reply, ...result.data } : reply)),
					};
				}
				return top;
			});
			cancelEdit();
		} catch (error) {
			alert("피드백 수정 중 오류가 발생했습니다.");
		} finally {
			isSaving = false;
		}
	};

	// 삭제 (soft delete)
	const removeFeedback = async (feedback, isReply) => {
		if (!confirm("이 피드백을 삭제하시겠습니까?")) return;

		try {
			const result = await deleteSoundImageFeedbackGen2(feedback.id);
			if (result.resultCode !== ErrorCodes.OK) {
				alert(result.resultDescKo || "피드백 삭제에 실패했습니다.");
				return;
			}

			if (isReply) {
				// 삭제된 답글은 목록에서 빠진다
				items = items.map((top) => ({
					...top,
					replies: (top.replies || []).filter((reply) => reply.id !== feedback.id),
				}));
			} else {
				// 답글이 남아 있으면 자리를 표시하고, 없으면 목록에서 빠진다
				items = items
					.map((top) => (top.id === feedback.id ? { ...top, isDeleted: true, content: "" } : top))
					.filter((top) => !(top.isDeleted && (top.replies || []).length === 0));
			}
			recountFromItems();
		} catch (error) {
			alert("피드백 삭제 중 오류가 발생했습니다.");
		}
	};

	// 해결 토글
	const toggleResolve = async (feedback) => {
		const nextResolved = !feedback.isResolved;
		try {
			const result = await resolveSoundImageFeedbackGen2({ id: feedback.id, isResolved: nextResolved });
			if (result.resultCode !== ErrorCodes.OK) {
				alert(result.resultDescKo || "해결 처리에 실패했습니다.");
				return;
			}
			items = items.map((top) =>
				top.id === feedback.id ? { ...top, ...result.data, replies: top.replies || [] } : top
			);
			recountFromItems();
		} catch (error) {
			alert("해결 처리 중 오류가 발생했습니다.");
		}
	};

	// 답글 입력창 토글
	const toggleReplyBox = (feedback) => {
		if (replyingToId === feedback.id) {
			replyingToId = null;
			replyContent = "";
			return;
		}
		replyingToId = feedback.id;
		replyContent = "";
	};
</script>

<div class="border-top pt-3">
	<div class="d-flex align-items-center gap-2 mb-2">
		<span class="fw-bold">피드백 {total}</span>
		{#if unresolvedCount > 0}
			<span class="badge bg-label-warning">미해결 {unresolvedCount}</span>
		{/if}
	</div>

	<!-- 새 피드백 입력 -->
	<div class="mb-3">
		<textarea
			class="form-control"
			rows="2"
			placeholder="이미지에 대한 피드백을 남겨주세요"
			maxlength={MAX_CONTENT_LENGTH}
			bind:value={newContent}
		></textarea>
		<div class="d-flex justify-content-between align-items-center mt-1">
			<span class="text-muted" style="font-size: 0.72rem;">{newContent.length}/{MAX_CONTENT_LENGTH}</span>
			<button class="btn btn-sm btn-primary" on:click={createFeedback} disabled={isSaving || !newContent.trim()}>
				{isSaving ? "등록 중..." : "등록"}
			</button>
		</div>
	</div>

	{#if isLoading}
		<div class="text-center text-muted py-3">
			<div class="spinner-border spinner-border-sm me-2" role="status"></div>
			피드백을 불러오는 중...
		</div>
	{:else if items.length === 0}
		<div class="text-center text-muted py-3 small">아직 피드백이 없습니다.</div>
	{:else}
		{#each items as top (top.id)}
			<div class="border-top py-2" in:fade={{ duration: 150 }}>
				{#if top.isDeleted}
					<div class="text-muted fst-italic small">삭제된 피드백입니다.</div>
				{:else}
					<div class="d-flex align-items-center justify-content-between flex-wrap gap-1">
						<div class="d-flex align-items-center gap-2">
							<span class="fw-semibold small">{top.authorNickName || "-"}</span>
							<span class="text-muted" style="font-size: 0.72rem;">{top.createdAt || ""}</span>
							{#if top.editedAt}
								<span class="text-muted" style="font-size: 0.72rem;">(수정됨)</span>
							{/if}
						</div>
						{#if top.isResolved}
							<span class="badge bg-label-success">해결됨</span>
						{:else}
							<span class="badge bg-label-warning">미해결</span>
						{/if}
					</div>

					{#if editingId === top.id}
						<div class="mt-1">
							<textarea class="form-control" rows="2" maxlength={MAX_CONTENT_LENGTH} bind:value={editContent}></textarea>
							<div class="mt-1 d-flex gap-1">
								<button class="btn btn-sm btn-primary" on:click={() => saveEdit(top)} disabled={isSaving}>저장</button>
								<button class="btn btn-sm btn-outline-secondary" on:click={cancelEdit}>취소</button>
							</div>
						</div>
					{:else}
						<div class="mt-1 small" style="white-space: pre-wrap;">{top.content}</div>

						<div class="mt-1 d-flex gap-1 flex-wrap">
							<button class="btn btn-sm btn-outline-secondary py-0" style="font-size: 0.72rem;" on:click={() => toggleReplyBox(top)}>
								답글
							</button>
							{#if top.isMine}
								<button class="btn btn-sm btn-outline-secondary py-0" style="font-size: 0.72rem;" on:click={() => startEdit(top)}>
									수정
								</button>
								<button class="btn btn-sm btn-outline-danger py-0" style="font-size: 0.72rem;" on:click={() => removeFeedback(top, false)}>
									삭제
								</button>
							{/if}
							<button
								class="btn btn-sm py-0 {top.isResolved ? 'btn-outline-secondary' : 'btn-outline-success'}"
								style="font-size: 0.72rem;"
								on:click={() => toggleResolve(top)}
							>
								{top.isResolved ? "해결 취소" : "해결 처리"}
							</button>
						</div>

						{#if top.isResolved && top.resolverNickName}
							<div class="text-muted mt-1" style="font-size: 0.72rem;">
								{top.resolverNickName}이 {top.resolvedAt || ""} 해결 처리
							</div>
						{/if}
					{/if}
				{/if}

				<!-- 답글 목록 -->
				{#each top.replies || [] as reply (reply.id)}
					<div class="mt-2 ps-3 border-start">
						<div class="d-flex align-items-center gap-2">
							<span class="fw-semibold small">{reply.authorNickName || "-"}</span>
							<span class="text-muted" style="font-size: 0.72rem;">{reply.createdAt || ""}</span>
							{#if reply.editedAt}
								<span class="text-muted" style="font-size: 0.72rem;">(수정됨)</span>
							{/if}
						</div>

						{#if editingId === reply.id}
							<div class="mt-1">
								<textarea class="form-control" rows="2" maxlength={MAX_CONTENT_LENGTH} bind:value={editContent}></textarea>
								<div class="mt-1 d-flex gap-1">
									<button class="btn btn-sm btn-primary" on:click={() => saveEdit(reply)} disabled={isSaving}>저장</button>
									<button class="btn btn-sm btn-outline-secondary" on:click={cancelEdit}>취소</button>
								</div>
							</div>
						{:else}
							<div class="mt-1 small" style="white-space: pre-wrap;">{reply.content}</div>
							{#if reply.isMine}
								<div class="mt-1 d-flex gap-1">
									<button class="btn btn-sm btn-outline-secondary py-0" style="font-size: 0.72rem;" on:click={() => startEdit(reply)}>
										수정
									</button>
									<button class="btn btn-sm btn-outline-danger py-0" style="font-size: 0.72rem;" on:click={() => removeFeedback(reply, true)}>
										삭제
									</button>
								</div>
							{/if}
						{/if}
					</div>
				{/each}

				<!-- 답글 입력창 -->
				{#if replyingToId === top.id}
					<div class="mt-2 ps-3">
						<textarea
							class="form-control"
							rows="2"
							placeholder="답글을 남겨주세요"
							maxlength={MAX_CONTENT_LENGTH}
							bind:value={replyContent}
						></textarea>
						<div class="mt-1 d-flex gap-1">
							<button class="btn btn-sm btn-primary" on:click={() => createReply(top)} disabled={isSaving || !replyContent.trim()}>
								답글 등록
							</button>
							<button class="btn btn-sm btn-outline-secondary" on:click={() => toggleReplyBox(top)}>취소</button>
						</div>
					</div>
				{/if}
			</div>
		{/each}
	{/if}
</div>
